import { useActiveContext } from "@/contexts/ActiveContextContext";
import { cn } from "@/utils/cn";
import {
  Bookmark,
  ExternalLink,
  Heart,
  MessageCircle,
  Monitor,
  Moon,
  MoreHorizontal,
  Music2,
  Play,
  Repeat2,
  Send as SendIcon,
  Share2,
  Smartphone,
  Sun,
  ThumbsUp,
  VideoOff,
} from "lucide-react";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { getPlatformName } from "@/utils/platformIcons";
import { getCtaLabel, getYoutubeId } from "../utils/adsUtils";

interface CampaignPreviewProps {
  platform: "meta" | "google_ads" | "linkedin" | "tiktok" | "chatgpt_ads";
  googleCampaignType?:
    | "search"
    | "display"
    | "video"
    | "performance_max"
    | "shopping";
  headline?: string;
  message?: string;
  description?: string;
  linkUrl?: string;
  imageUrl?: string;
  videoUrl?: string;
  ctaText?: string;
  businessName?: string;
  sitelinks?: Array<{
    title: string;
    description1: string;
    description2: string;
    finalUrl: string;
  }>;
  postMediaUrls?: Array<{
    url: string;
    type: "image" | "video";
    thumbnail: string;
  }>;
}

type DeviceView = "mobile" | "desktop";

const CampaignPreview: React.FC<CampaignPreviewProps> = ({
  platform,
  googleCampaignType = "search",
  headline,
  message = "",
  description = "",
  linkUrl,
  imageUrl,
  videoUrl,
  ctaText = "LEARN_MORE",
  businessName,
  sitelinks = [],
  postMediaUrls = [],
}) => {
  const [deviceView, setDeviceView] = useState<DeviceView>("mobile");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [subPlatform, setSubPlatform] = useState<"facebook" | "instagram">(
    platform === "meta" ? "facebook" : "facebook",
  );
  const [captionExpanded, setCaptionExpanded] = useState(false);
  // Se o texto realmente transbordou o espaço visível do card (medido no
  // DOM, não estimado por contagem de caracteres) — só então o botão
  // "ver mais" aparece, exatamente como a rede real faria.
  const [captionClipped, setCaptionClipped] = useState(false);
  const captionRef = useRef<HTMLSpanElement>(null);
  const { activeContext } = useActiveContext();
  const { t } = useTranslation();

  // Reseta o estado "ver mais" ao trocar de plataforma/aba — evita que a
  // legenda continue expandida de uma prévia para outra sem relação.
  React.useEffect(() => {
    setCaptionExpanded(false);
  }, [platform, subPlatform]);

  // Mede se o texto colapsado (com o line-clamp aplicado) está realmente
  // cortando conteúdo — com `-webkit-line-clamp`, `scrollHeight` continua
  // refletindo a altura total do texto mesmo com o clamp ativo, então
  // comparar com `clientHeight` (a altura visível) detecta o corte real.
  useLayoutEffect(() => {
    if (captionExpanded) return;
    const el = captionRef.current;
    if (!el) {
      setCaptionClipped(false);
      return;
    }
    setCaptionClipped(el.scrollHeight - el.clientHeight > 1);
  }, [message, platform, subPlatform, deviceView, captionExpanded]);

  const brandName =
    businessName?.trim() ||
    activeContext.clientCompany?.name ||
    activeContext.organization?.name ||
    t("Sua Marca");
  const brandHandle = brandName.toLowerCase().replace(/\s+/g, ".");
  const brandLogo =
    activeContext.clientCompany?.logo_url ||
    activeContext.organization?.logo_url;
  const brandInitial = brandName.charAt(0).toUpperCase();

  // Helper to render text with clickable hashtags (blue style like in PreviewPostModal)
  const renderTextWithHashtags = (text: string, colorClass: string) => {
    if (!text) return null;
    const words = text.split(/(\s+)/);
    return words.map((word, i) => {
      if (word.startsWith("#") && word.length > 1) {
        return (
          <span
            key={i}
            className={cn("cursor-pointer hover:underline", colorClass)}
          >
            {word}
          </span>
        );
      }
      return word;
    });
  };

  // Legendas longas são truncadas (como nas redes reais) com um botão
  // "ver mais/ver menos". `clampClass` define quantas linhas cabem antes
  // do corte — calibrado para caber ~o "recomendado" de exibição de cada
  // plataforma (ver AD_TEXT_LIMITS em charLimits.ts) — mas o botão só
  // aparece se o texto realmente transbordou esse espaço (medido no DOM
  // via `captionClipped`), não por uma contagem de caracteres aproximada.
  const renderCaption = (
    text: string,
    colorClass: string,
    clampClass:
      | "line-clamp-2"
      | "line-clamp-3"
      | "line-clamp-4" = "line-clamp-3",
    emptyFallback: string = t("Seu texto principal aparecerá aqui..."),
  ) => {
    return (
      <>
        <span
          ref={captionRef}
          className={!captionExpanded ? clampClass : undefined}
        >
          {renderTextWithHashtags(text, colorClass) || emptyFallback}
        </span>
        {captionClipped && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCaptionExpanded((v) => !v);
            }}
            className={cn(
              "ml-1 font-bold hover:underline whitespace-nowrap",
              colorClass,
            )}
          >
            {captionExpanded ? t("ver menos") : t("ver mais")}
          </button>
        )}
      </>
    );
  };

  const renderMedia = () => {
    const mediaToRender =
      postMediaUrls.length > 0
        ? postMediaUrls
        : imageUrl || videoUrl
          ? [
              {
                url: (videoUrl || imageUrl)!,
                type: videoUrl ? "video" : ("image" as any),
                thumbnail: "",
              },
            ]
          : [];

    if (mediaToRender.length === 0) {
      return (
        <div
          className={cn(
            "w-full bg-purple-50/20 flex flex-col items-center justify-center text-purple-200 italic p-12 text-center text-[10px] font-black uppercase tracking-[0.2em] border-b border-purple-50",
            subPlatform === "instagram" ? "aspect-square" : "aspect-video",
          )}
        >
          {t("Visual Placeholder")}
        </div>
      );
    }

    const currentMedia =
      mediaToRender[currentMediaIndex % mediaToRender.length];

    const nextMedia = () =>
      setCurrentMediaIndex((prev) => (prev + 1) % mediaToRender.length);
    const prevMedia = () =>
      setCurrentMediaIndex(
        (prev) => (prev - 1 + mediaToRender.length) % mediaToRender.length,
      );

    return (
      <div className="relative group w-full overflow-hidden bg-gray-900">
        <div
          className={cn(
            "w-full flex items-center justify-center transition-all",
            subPlatform === "instagram"
              ? "aspect-square"
              : "h-auto max-h-[450px]",
          )}
        >
          {currentMedia.type === "video" ? (
            <div className="w-full h-full flex items-center justify-center bg-black min-h-[300px]">
              {currentMedia.url.includes("youtube.com") ||
              currentMedia.url.includes("youtu.be") ? (
                (() => {
                  const ytId = getYoutubeId(currentMedia.url);
                  return ytId ? (
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${ytId}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-white/40">
                      <VideoOff className="w-8 h-8" />
                      <span className="text-xs font-medium">
                        {t("Link do YouTube inválido")}
                      </span>
                    </div>
                  );
                })()
              ) : (
                <video
                  src={currentMedia.url}
                  className="w-full h-full max-h-[450px] object-contain"
                  controls
                  autoPlay
                  muted
                  loop
                />
              )}
            </div>
          ) : (
            <img
              src={currentMedia.url}
              alt="Ad Visual"
              className="w-full h-full max-h-[450px] object-contain bg-gray-50"
            />
          )}
        </div>

        {mediaToRender.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevMedia();
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all z-20 hover:bg-black/80"
              aria-label={t("Mídia anterior")}
            >
              &larr;
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextMedia();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all z-20 hover:bg-black/80"
              aria-label={t("Próxima mídia")}
            >
              &rarr;
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {mediaToRender.map((_, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "w-1.5 h-1.5 rounded-full transition-all shadow-xs",
                    idx === currentMediaIndex % mediaToRender.length
                      ? "bg-white scale-125"
                      : "bg-white/40",
                  )}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderFacebook = () => (
    <div
      className={cn(
        "w-full font-sans transition-all duration-500",
        isDarkMode ? "bg-[#242526] text-[#e4e6eb]" : "bg-white text-[#050505]",
      )}
    >
      {/* Header Browser (Desktop only) */}
      {deviceView === "desktop" && (
        <div
          className={cn(
            "h-4 border-b flex items-center px-3 gap-1",
            isDarkMode
              ? "bg-[#18191a] border-white/10"
              : "bg-[#f0f2f5] border-gray-200",
          )}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
        </div>
      )}

      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center ring-1 overflow-hidden bg-white",
              isDarkMode ? "ring-white/10" : "ring-black/5",
            )}
          >
            {brandLogo ? (
              <img
                src={brandLogo}
                alt="Logo"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="font-bold text-lg text-gray-600">
                {brandInitial}
              </span>
            )}
          </div>
          <div>
            <div
              className={cn(
                "font-bold text-[15px] leading-tight",
                isDarkMode ? "text-white" : "text-[#050505]",
              )}
            >
              {brandName}
            </div>
            <div
              className={cn(
                "text-[13px] flex items-center gap-1 leading-tight",
                isDarkMode ? "text-[#b0b3b8]" : "text-[#65676b]",
              )}
            >
              {t("Patrocinado")} • 🌎
            </div>
          </div>
        </div>
        <MoreHorizontal className="h-5 w-5 opacity-40" />
      </div>

      <div className="px-4 pb-3 text-[15px] leading-normal whitespace-pre-wrap wrap-break-word">
        {renderCaption(
          message,
          isDarkMode ? "text-[#4591ff]" : "text-[#216fdb]",
        )}
      </div>

      {renderMedia()}

      <div
        className={cn(
          "px-3 py-2.5 flex justify-between items-center border-t",
          isDarkMode
            ? "bg-[#2f3031] border-white/5"
            : "bg-[#f0f2f5] border-gray-200",
        )}
      >
        <div className="flex-1 min-w-0 pr-2">
          <div
            className={cn(
              "text-[12px] uppercase tracking-wide truncate",
              isDarkMode ? "text-[#b0b3b8]" : "text-[#65676b]",
            )}
          >
            {linkUrl?.replace(/https?:\/\//i, "").split("/")[0] ||
              t("SITE DA MARCA")}
          </div>
          <div
            className={cn(
              "font-bold text-[14px] truncate",
              isDarkMode ? "text-white" : "text-[#050505]",
            )}
          >
            {headline || t("Seu Título Aqui")}
          </div>
        </div>
        <button
          className={cn(
            "font-bold border-none h-7 px-2.5 rounded-md shrink-0 text-[12px]",
            isDarkMode
              ? "bg-white/10 text-white"
              : "bg-[#e4e6eb] text-[#050505]",
          )}
        >
          {getCtaLabel(ctaText, t)}
        </button>
      </div>

      {/* Interaction Bar like PreviewPostModal */}
      <div className="mx-4 py-3 flex items-center justify-between border-b border-[#dadde1] gap-2">
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="flex -space-x-1">
            <div className="bg-[#1877f2] rounded-full p-1 ring-1 ring-white z-10">
              <ThumbsUp className="h-2.5 w-2.5 text-white fill-white" />
            </div>
            <div className="bg-[#f33e58] rounded-full p-1 ring-1 ring-white">
              <Heart className="h-2.5 w-2.5 text-white fill-white" />
            </div>
          </div>
          <span className="text-[13px] text-[#65676b]">124</span>
        </div>
        <div className="text-[13px] text-[#65676b] hover:underline cursor-pointer truncate text-right">
          {t("12 coment. • 8 partilh.")}
        </div>
      </div>

      <div className="mx-4 py-1 flex items-center justify-around text-[#65676b] font-semibold">
        <div className="flex-1 flex items-center justify-center gap-2 py-1.5 text-[14px] hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
          <ThumbsUp className="h-5 w-5" /> {t("Curtir")}
        </div>
        <div className="flex-1 flex items-center justify-center gap-2 py-1.5 text-[14px] hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
          <MessageCircle className="h-5 w-5" /> {t("Comentar")}
        </div>
        <div className="flex-1 flex items-center justify-center gap-2 py-1.5 text-[14px] hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
          <Share2 className="h-5 w-5" /> {t("Enviar")}
        </div>
      </div>
    </div>
  );

  const renderInstagram = () => (
    <div
      className={cn(
        "w-full transition-all duration-500",
        isDarkMode ? "bg-black text-white" : "bg-white text-black",
      )}
    >
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-[34px] h-[34px] rounded-full p-[1.5px] bg-linear-to-tr from-[#FFD600] via-[#FF7A00] via-[#FF0069] via-[#D300C5] to-[#7638FA]">
            <div
              className={cn(
                "w-full h-full rounded-full p-[1.5px]",
                isDarkMode ? "bg-black" : "bg-white",
              )}
            >
              <div className="w-full h-full bg-white rounded-full border border-black/5 overflow-hidden flex items-center justify-center">
                {brandLogo ? (
                  <img
                    src={brandLogo}
                    alt="Logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-[10px] font-black uppercase text-gray-600">
                    {brandInitial}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="font-bold text-[13px] tracking-tight leading-tight flex items-center gap-1">
              {brandHandle}
            </div>
            <div
              className={cn(
                "text-[11px] leading-tight",
                isDarkMode ? "text-gray-400" : "text-gray-900",
              )}
            >
              {t("Patrocinado")}
            </div>
          </div>
        </div>
        <MoreHorizontal className="h-5 w-5" />
      </div>
      {renderMedia()}
      <div className="bg-[#0095f6] hover:bg-[#1877f2] transition-colors text-white h-[44px] flex items-center justify-between px-4 cursor-pointer">
        <span className="font-bold text-[14px]">{getCtaLabel(ctaText, t)}</span>
        <ExternalLink className="h-4 w-4" />
      </div>
      <div className="px-3 pt-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-4">
            <Heart className="h-[24px] w-[24px]" />
            <MessageCircle className="h-[24px] w-[24px]" />
            <SendIcon className="h-[22px] w-[22px]" />
          </div>
          <Bookmark className="h-[24px] w-[24px]" />
        </div>
        <div className="text-[14px] leading-relaxed mb-3">
          <span className="font-extrabold mr-1.5">{brandHandle}</span>
          <span className="font-bold text-blue-600 mr-1.5">{headline}</span>
          {renderCaption(
            message,
            "text-blue-900",
            "line-clamp-3",
            t("Sua legenda..."),
          )}
        </div>
      </div>
    </div>
  );

  const googleSitelinksBlock = sitelinks && sitelinks.some((s) => s.title) && (
    <div
      className={cn(
        "grid grid-cols-2 gap-3 mt-4 pt-4 border-t",
        isDarkMode ? "border-white/10" : "border-gray-100",
      )}
    >
      {sitelinks
        .filter((s) => s.title)
        .map((s, i) => (
          <div key={i} className="space-y-0.5">
            <div
              className={cn(
                "text-[13px] font-medium underline truncate",
                isDarkMode ? "text-[#8ab4f8]" : "text-[#1a73e8]",
              )}
            >
              {s.title}
            </div>
            <div
              className={cn(
                "text-[11px] line-clamp-1",
                isDarkMode ? "text-gray-400" : "text-gray-500",
              )}
            >
              {s.description1}
            </div>
          </div>
        ))}
    </div>
  );

  // Anúncio de Pesquisa: só texto — sem avatar, sem imagem, sem botão CTA
  // grande. É assim que um anúncio de texto do Google realmente aparece.
  const renderGoogleSearch = () => (
    <div className="p-4">
      <div className="flex items-center gap-1.5 text-[13px]">
        <span
          className={cn(
            "font-bold border rounded-sm px-1",
            isDarkMode
              ? "text-white/90 border-white/30"
              : "text-gray-800 border-gray-400",
          )}
        >
          {t("Anúncio")}
        </span>
        <span
          className={cn(
            "truncate",
            isDarkMode ? "text-gray-300" : "text-gray-700",
          )}
        >
          {linkUrl?.replace(/https?:\/\//i, "") || "www.seusite.com.br"}
        </span>
      </div>
      <div
        className={cn(
          "text-xl mt-1 leading-tight",
          isDarkMode ? "text-[#8ab4f8]" : "text-[#1a0dab]",
        )}
      >
        {headline || t("Seu Título Impactante do Google")}
      </div>
      <div
        className={cn(
          "text-[14px] mt-1.5 leading-relaxed whitespace-pre-wrap wrap-break-word",
          isDarkMode ? "text-gray-300" : "text-gray-600",
        )}
      >
        {renderTextWithHashtags(
          description || message,
          isDarkMode ? "text-[#8ab4f8]" : "text-[#1a73e8]",
        ) || t("Sua descrição de anúncio Google...")}
      </div>
      {googleSitelinksBlock}
    </div>
  );

  // YouTube Video: thumbnail com play, canal e CTA — formato de in-stream/discovery ad.
  const renderGoogleVideo = () => (
    <div>
      <div className="relative aspect-video bg-black overflow-hidden">
        {imageUrl || videoUrl || postMediaUrls.length > 0 ? (
          renderMedia()
        ) : (
          <div className="w-full h-full bg-linear-to-br from-gray-800 to-black" />
        )}
        <div className="absolute top-2 left-2 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm">
          {t("Anúncio")}
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
            <Play className="w-6 h-6 text-white fill-white ml-0.5" />
          </div>
        </div>
      </div>
      <div className="p-3 flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 font-bold text-sm shrink-0 overflow-hidden">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt="Logo"
              className="w-full h-full object-cover"
            />
          ) : (
            brandInitial
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "text-sm font-bold truncate leading-tight",
              isDarkMode ? "text-white" : "text-gray-900",
            )}
          >
            {headline || t("Seu Título Impactante do Google")}
          </div>
          <div
            className={cn(
              "text-xs truncate mt-0.5",
              isDarkMode ? "text-gray-400" : "text-gray-500",
            )}
          >
            {brandName}
          </div>
        </div>
        <button className="shrink-0 bg-transparent border border-gray-400 hover:bg-gray-100 text-gray-800 font-bold px-3 py-1.5 rounded-sm text-xs">
          {getCtaLabel(ctaText, t)}
        </button>
      </div>
    </div>
  );

  // Display / Performance Max: card com imagem, avatar e CTA — Display é
  // fundamentalmente um banner de imagem, e PMax mistura formatos (esse
  // layout misto representa bem os dois).
  const renderGoogleDisplay = () => (
    <>
      <div className="p-4 flex items-center gap-3">
        <div
          className={cn(
            "w-10 h-10 rounded-full border flex items-center justify-center overflow-hidden shadow-xs shrink-0 bg-white",
            isDarkMode ? "border-white/10" : "border-gray-100",
          )}
        >
          {brandLogo ? (
            <img
              src={brandLogo}
              alt="Logo"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="font-bold text-lg text-gray-600">
              {brandInitial}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "font-bold text-[15px] flex items-center gap-1.5 leading-tight",
              isDarkMode ? "text-white" : "text-gray-900",
            )}
          >
            <span className="truncate">{brandName}</span>
          </div>
          <div
            className={cn(
              "text-[12px] mt-0.5",
              isDarkMode ? "text-gray-400" : "text-gray-500",
            )}
          >
            {googleCampaignType === "performance_max"
              ? t("Performance Max")
              : t("Anúncio de Display")}{" "}
            • {getPlatformName("google_ads")}
          </div>
        </div>
        <MoreHorizontal className="h-5 w-5 opacity-40" />
      </div>

      <div className="px-5 pb-3 pt-1 space-y-2">
        <div
          className={cn(
            "text-[14px] underline mb-1",
            isDarkMode ? "text-[#8ab4f8]" : "text-[#1a73e8]",
          )}
        >
          {linkUrl || "www.seusite.com.br"}
        </div>
        <div
          className={cn(
            "text-xl font-medium leading-tight",
            isDarkMode ? "text-[#8ab4f8]" : "text-[#1a73e8]",
          )}
        >
          {headline || t("Seu Título Impactante do Google")}
        </div>
        <div
          className={cn(
            "text-[14px] leading-relaxed whitespace-pre-wrap wrap-break-word",
            isDarkMode ? "text-gray-300" : "text-gray-600",
          )}
        >
          {renderTextWithHashtags(
            description || message,
            isDarkMode ? "text-[#8ab4f8]" : "text-[#1a73e8]",
          ) || t("Sua descrição de anúncio Google...")}
        </div>

        {googleSitelinksBlock}
      </div>

      {(imageUrl || videoUrl || postMediaUrls.length > 0) && (
        <div className="mt-4 px-5 pb-6">{renderMedia()}</div>
      )}

      <div
        className={cn(
          "p-5 pt-3 border-t flex justify-center w-full",
          isDarkMode ? "border-white/5" : "border-gray-50",
        )}
      >
        <div className="w-full h-10 px-6 bg-[#1a73e8] hover:bg-[#1765cc] text-white font-bold rounded-full shadow-md flex items-center justify-center gap-2 cursor-pointer transition-all">
          <div className="px-4 py-2.5">
            <span className="text-[13px]">{getCtaLabel(ctaText, t)}</span>
          </div>
          <ExternalLink className="h-3.5 w-3.5 opacity-90" />
        </div>
      </div>
    </>
  );

  const renderGoogle = () => (
    <div
      className={cn(
        "w-full font-sans transition-all duration-500",
        isDarkMode ? "bg-[#202124] text-[#e8eaed]" : "bg-white text-[#202124]",
      )}
    >
      {/* Header Browser (Desktop only) */}
      {deviceView === "desktop" && (
        <div
          className={cn(
            "h-4 border-b flex items-center px-3 gap-1",
            isDarkMode
              ? "bg-[#202124] border-white/5"
              : "bg-gray-50 border-gray-100",
          )}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
        </div>
      )}

      {googleCampaignType === "search"
        ? renderGoogleSearch()
        : googleCampaignType === "video"
          ? renderGoogleVideo()
          : renderGoogleDisplay()}
    </div>
  );

  const renderLinkedin = () => {
    const mediaToRender =
      postMediaUrls.length > 0
        ? postMediaUrls
        : imageUrl || videoUrl
          ? [
              {
                url: (videoUrl || imageUrl)!,
                type: videoUrl ? "video" : ("image" as any),
                thumbnail: "",
              },
            ]
          : [];

    const getPostImageUrl = () => {
      if (mediaToRender.length > 0) return mediaToRender[0].url;
      return null;
    };

    return (
      <div
        className={cn(
          "w-full font-sans transition-all duration-500 p-4 flex flex-col gap-4",
          isDarkMode ? "bg-black text-white" : "bg-[#f3f2ef] text-gray-900",
        )}
      >
        {/* Authentic LinkedIn Live Preview Mockup */}
        <div
          className={cn(
            "border rounded-xl p-4 shadow-xs flex flex-col gap-3",
            isDarkMode
              ? "border-[#38434f] bg-[#1d2226]"
              : "border-gray-200 bg-white",
          )}
        >
          <p
            className={cn(
              "text-[10px] leading-normal border-b pb-2",
              isDarkMode
                ? "text-white/60 border-white/10"
                : "text-gray-500 border-gray-100",
            )}
          >
            <span
              className={cn(
                "font-semibold",
                isDarkMode ? "text-white/90" : "text-gray-700",
              )}
            >
              {t("{count} conexões suas", { count: 162 })}
            </span>{" "}
            {t("seguem esta Page")}
          </p>

          <div className="flex items-start gap-2.5">
            {/* Brand avatar */}
            <div
              className={cn(
                "w-10 h-10 bg-white border rounded-lg flex items-center justify-center font-bold text-gray-600 text-lg shadow-xs overflow-hidden shrink-0",
                isDarkMode ? "border-white/10" : "border-gray-200",
              )}
            >
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                brandInitial
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4
                className={cn(
                  "text-xs font-bold truncate",
                  isDarkMode ? "text-white" : "text-gray-900",
                )}
              >
                {brandName}
              </h4>
              <p
                className={cn(
                  "text-[10px]",
                  isDarkMode ? "text-white/60" : "text-gray-500",
                )}
              >
                {t("{count} seguidores", { count: 409 })}
              </p>
              <p
                className={cn(
                  "text-[10px] flex items-center gap-1",
                  isDarkMode ? "text-white/60" : "text-gray-500",
                )}
              >
                {t("Promovida")} •{" "}
                <ExternalLink
                  className={cn(
                    "w-3 h-3",
                    isDarkMode ? "text-white/40" : "text-gray-400",
                  )}
                />
              </p>
            </div>
          </div>

          {/* Caption Text - Dynamic */}
          <p
            className={cn(
              "text-xs leading-relaxed font-normal whitespace-pre-wrap wrap-break-word",
              isDarkMode ? "text-white/90" : "text-gray-800",
            )}
          >
            {renderCaption(
              message,
              isDarkMode
                ? "text-[#6dabf5] font-semibold"
                : "text-[#0077b5] font-semibold",
              "line-clamp-4",
              t("Seu texto do post aparecerá aqui..."),
            )}
          </p>

          {/* Creative Banner Box */}
          <div
            className={cn(
              "w-full rounded-lg overflow-hidden border aspect-video relative flex items-center justify-center bg-linear-to-br from-[#0b1320] to-[#121c2c]",
              isDarkMode ? "border-white/10" : "border-gray-100",
            )}
          >
            {getPostImageUrl() ? (
              mediaToRender[0]?.type === "video" ? (
                mediaToRender[0]?.url.includes("youtube.com") ||
                mediaToRender[0]?.url.includes("youtu.be") ? (
                  (() => {
                    const ytId = getYoutubeId(mediaToRender[0]?.url || "");
                    return ytId ? (
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${ytId}`}
                        title="YouTube video player"
                        frameBorder="0"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-white/40">
                        <VideoOff className="w-8 h-8" />
                        <span className="text-xs font-medium">
                          {t("Link do YouTube inválido")}
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <video
                    src={mediaToRender[0]?.url}
                    className="w-full h-full object-contain bg-black"
                    controls
                  />
                )
              ) : (
                <img
                  src={getPostImageUrl()!}
                  alt="Ad preview creative"
                  className="w-full h-full object-cover"
                />
              )
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-6 text-white w-full h-full">
                <span className="text-[10px] font-bold tracking-widest text-[#0077b5]/80 uppercase">
                  {brandName}
                </span>
                <h3 className="text-base font-black uppercase text-center mt-2 tracking-wide text-white leading-tight max-w-[200px]">
                  {message?.split("\n")[0]?.substring(0, 40) ||
                    t("Filtre Interessados Reais")}
                </h3>
              </div>
            )}
          </div>

          {/* LinkedIn Bottom Promoted CTA Bar */}
          <div
            className={cn(
              "flex items-center justify-between gap-2 border rounded-lg px-3 py-2",
              isDarkMode
                ? "bg-white/5 border-white/10"
                : "bg-gray-50 border-gray-100",
            )}
          >
            <div className="flex-1 min-w-0">
              <span
                className={cn(
                  "block text-[10px] uppercase tracking-widest truncate",
                  isDarkMode ? "text-white/40" : "text-gray-400",
                )}
              >
                {linkUrl?.replace(/https?:\/\//i, "").split("/")[0] ||
                  "virtusautomation.com"}
              </span>
              <span
                className={cn(
                  "block text-xs font-bold truncate",
                  isDarkMode ? "text-white/90" : "text-gray-700",
                )}
              >
                {headline ||
                  (ctaText === "SIGN_UP"
                    ? t("Formulário de Contato Inteligente")
                    : t("Soluções de Automação para Negócios"))}
              </span>
            </div>
            <button
              className={cn(
                "shrink-0 border px-3 py-1 rounded-full text-[11px] font-bold transition-all shadow-xs",
                isDarkMode
                  ? "bg-transparent border-[#6dabf5] text-[#6dabf5] hover:bg-[#6dabf5]/10"
                  : "bg-white border-[#0077b5] text-[#0077b5] hover:bg-[#0077b5]/5",
              )}
            >
              {getCtaLabel(ctaText, t)}
            </button>
          </div>

          {/* Reaction summary + ações — LinkedIn tem 4 ações (Curtir, Comentar,
              Compartilhar, Enviar), diferente das 3 do Facebook, e reações
              próprias (👍❤️💡) em vez do polegar+coração do Facebook. */}
          <div
            className={cn(
              "flex items-center justify-between pt-2 border-t text-[11px]",
              isDarkMode
                ? "border-white/10 text-white/50"
                : "border-gray-100 text-gray-500",
            )}
          >
            <div className="flex items-center gap-1">
              <div className="flex -space-x-1">
                <div
                  className={cn(
                    "bg-[#0a66c2] rounded-full p-1 ring-2 z-20",
                    isDarkMode ? "ring-[#1d2226]" : "ring-white",
                  )}
                >
                  <ThumbsUp className="h-2 w-2 text-white fill-white" />
                </div>
                <div
                  className={cn(
                    "bg-[#e06847] rounded-full p-1 ring-2 z-10",
                    isDarkMode ? "ring-[#1d2226]" : "ring-white",
                  )}
                >
                  <Heart className="h-2 w-2 text-white fill-white" />
                </div>
              </div>
              <span>42</span>
            </div>
            <span className="hover:underline cursor-pointer">
              {t("12 comentários • 3 compartilhamentos")}
            </span>
          </div>

          <div
            className={cn(
              "flex items-center justify-around pt-1 font-semibold text-[11px]",
              isDarkMode ? "text-white/60" : "text-gray-600",
            )}
          >
            <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg cursor-pointer hover:bg-black/5">
              <ThumbsUp className="h-4 w-4" /> {t("Gostei")}
            </div>
            <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg cursor-pointer hover:bg-black/5">
              <MessageCircle className="h-4 w-4" /> {t("Comentar")}
            </div>
            <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg cursor-pointer hover:bg-black/5">
              <Repeat2 className="h-4 w-4" /> {t("Compartilhar")}
            </div>
            <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg cursor-pointer hover:bg-black/5">
              <SendIcon className="h-4 w-4" /> {t("Enviar")}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // TikTok In-Feed Ad: vídeo/imagem vertical em tela cheia, trilha de ações
  // na lateral direita, legenda + CTA no rodapé — nada parecido com o card
  // branco horizontal do Facebook/Instagram. O app do TikTok não tem modo
  // claro real, então o preview permanece escuro independente do toggle
  // (mantido só por consistência com as outras plataformas).
  const renderTiktok = () => {
    const hasMedia = !!(imageUrl || videoUrl || postMediaUrls.length > 0);
    return (
      <div className="relative w-full aspect-9/16 bg-black overflow-hidden">
        <div className="absolute inset-0">
          {hasMedia ? (
            renderMedia()
          ) : (
            <div className="w-full h-full bg-linear-to-br from-gray-900 via-black to-gray-900" />
          )}
        </div>
        <div className="absolute inset-0 bg-linear-to-t from-black/85 via-black/10 to-black/40 pointer-events-none" />

        {/* Trilha de ações lateral */}
        <div className="absolute right-2.5 bottom-24 flex flex-col items-center gap-4 text-white">
          <div className="w-10 h-10 rounded-full bg-white border-2 border-white flex items-center justify-center overflow-hidden shrink-0 shadow-md">
            {brandLogo ? (
              <img
                src={brandLogo}
                alt="Logo"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="font-bold text-xs text-gray-600">
                {brandInitial}
              </span>
            )}
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <Heart className="w-7 h-7 fill-white drop-shadow-md" />
            <span className="text-[10px] font-bold drop-shadow-md">12.4K</span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <MessageCircle className="w-7 h-7 fill-white drop-shadow-md" />
            <span className="text-[10px] font-bold drop-shadow-md">328</span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <Share2 className="w-7 h-7 fill-white drop-shadow-md" />
            <span className="text-[10px] font-bold drop-shadow-md">
              {t("Compartilhar")}
            </span>
          </div>
          <div className="w-8 h-8 rounded-full bg-linear-to-br from-gray-700 to-black border border-white/30 flex items-center justify-center">
            <Music2 className="w-3.5 h-3.5 text-white" />
          </div>
        </div>

        {/* Legenda + CTA no rodapé */}
        <div className="absolute bottom-0 left-0 right-14 p-3.5 text-white space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold">
            <span className="truncate">@{brandHandle}</span>
            <span className="text-white/70 font-semibold text-[10px]">
              • {t("Patrocinado")}
            </span>
          </div>
          <p className="text-xs leading-snug whitespace-pre-wrap wrap-break-word drop-shadow-md">
            {renderCaption(
              message,
              "text-white font-bold",
              "line-clamp-2",
              t("Seu texto aparecerá aqui..."),
            )}
          </p>
          <button className="bg-white text-black font-bold text-xs px-4 py-2 rounded-md shadow-lg">
            {getCtaLabel(ctaText, t)}
          </button>
        </div>
      </div>
    );
  };

  // ChatGPT Ads "chat_card": aparece como um card dentro da conversa —
  // avatar do assistente, uma linha de resposta simulada, e o card com
  // imagem + título + corpo + botão de link (o único formato de criativo
  // documentado pela Advertiser API da OpenAI).
  const renderChatGptAds = () => (
    <div
      className={cn(
        "w-full font-sans transition-all duration-500 p-4 space-y-3",
        isDarkMode ? "bg-[#212121] text-white" : "bg-white text-gray-900",
      )}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center shrink-0 text-white text-xs font-black">
          AI
        </div>
        <p
          className={cn(
            "text-[13px] leading-relaxed pt-1",
            isDarkMode ? "text-white/80" : "text-gray-700",
          )}
        >
          {t(
            "Aqui está uma opção que pode te interessar com base no que conversamos:",
          )}
        </p>
      </div>

      <div
        className={cn(
          "ml-9 rounded-2xl border overflow-hidden shadow-xs",
          isDarkMode
            ? "border-white/10 bg-[#2a2a2a]"
            : "border-gray-200 bg-white",
        )}
      >
        <div className="aspect-video bg-gray-900">{renderMedia()}</div>
        <div className="p-3.5 space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">
            {t("Patrocinado")}
          </div>
          <div
            className={cn(
              "text-sm font-bold leading-tight",
              isDarkMode ? "text-white" : "text-gray-900",
            )}
          >
            {headline || t("Título do seu anúncio")}
          </div>
          <p
            className={cn(
              "text-xs leading-snug line-clamp-2",
              isDarkMode ? "text-white/60" : "text-gray-500",
            )}
          >
            {message || t("Corpo curto do anúncio aparecerá aqui...")}
          </p>
          <button className="mt-2 w-full h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1.5">
            {linkUrl?.replace(/https?:\/\//i, "").split("/")[0] ||
              t("Visitar site")}
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-purple-100 bg-white/95 backdrop-blur-md sticky top-0 z-30">
        <div className="flex items-center bg-gray-100/90 p-0.5 rounded-xl border border-gray-200/60 shadow-inner">
          <button
            type="button"
            onClick={() => setDeviceView("mobile")}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              deviceView === "mobile"
                ? "bg-white shadow-xs text-purple-600 font-bold"
                : "text-gray-400 hover:text-gray-600",
            )}
            title={t("Visualização Mobile")}
          >
            <Smartphone className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setDeviceView("desktop")}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              deviceView === "desktop"
                ? "bg-white shadow-xs text-purple-600 font-bold"
                : "text-gray-400 hover:text-gray-600",
            )}
            title={t("Visualização Desktop")}
          >
            <Monitor className="h-4 w-4" />
          </button>
          <div className="w-px h-4 bg-gray-300 mx-1" />
          <button
            type="button"
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              isDarkMode
                ? "bg-purple-600 text-white shadow-xs"
                : "text-gray-400 hover:text-gray-600",
            )}
            title={t("Alternar Modo Escuro")}
          >
            {isDarkMode ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
        </div>

        {platform === "meta" && (
          <div className="flex items-center bg-gray-100/90 p-0.5 rounded-xl border border-gray-200/60">
            <button
              type="button"
              onClick={() => setSubPlatform("facebook")}
              className={cn(
                "px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all",
                subPlatform === "facebook"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "text-gray-400 hover:text-gray-600",
              )}
            >
              Facebook
            </button>
            <button
              type="button"
              onClick={() => setSubPlatform("instagram")}
              className={cn(
                "px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all",
                subPlatform === "instagram"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "text-gray-400 hover:text-gray-600",
              )}
            >
              Instagram
            </button>
          </div>
        )}
      </div>

      {/* Simulation Area */}
      <div className="flex-1 min-h-[400px] flex items-center justify-center p-4 sm:p-6 bg-slate-50/70 relative overflow-y-auto">
        <div
          className={cn(
            "transition-all duration-300",
            deviceView === "mobile"
              ? "w-[320px] sm:w-[340px]"
              : "w-full max-w-[480px]",
            "shadow-xl rounded-2xl sm:rounded-3xl overflow-hidden border border-purple-200/50 bg-white",
          )}
        >
          {platform === "google_ads"
            ? renderGoogle()
            : platform === "linkedin"
              ? renderLinkedin()
              : platform === "tiktok"
                ? renderTiktok()
                : platform === "chatgpt_ads"
                  ? renderChatGptAds()
                  : subPlatform === "instagram"
                    ? renderInstagram()
                    : renderFacebook()}
        </div>
      </div>
    </div>
  );
};

export default CampaignPreview;
